import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  Plus,
  Trash2,
  Package,
  User,
  CreditCard,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface CreateLayawayTabProps {
  // Items and inventory
  filteredAndSortedItems: any[];
  itemSearchTerm: string;
  setItemSearchTerm: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;

  // Cart
  cart: any[];
  addToCart: (item: any, quantity?: number, unitPrice?: number, vatEnabled?: boolean) => void;
  removeFromCart: (id: string) => void;
  updateCartQuantity: (id: string, quantity: number) => void;
  updateCartItemPrice: (id: string, price: number) => void;
  updateCartItemVat: (id: string, vatEnabled: boolean) => void;

  // Customer
  customer: { name: string; phone: string; address: string };
  setCustomer: (customer: any) => void;
  customerOptions: Array<{ id: string; name: string; phone?: string; address?: string }>;
  onCreateCustomer: (customer: { name: string; phone: string; address: string }) => void;

  // Payment
  productHandling: 'hold' | 'handover';
  setProductHandling: (value: 'hold' | 'handover') => void;
  paymentScheduleMonths: number;
  setPaymentScheduleMonths: (value: number) => void;
  initialPayment: number;
  setInitialPayment: (value: number) => void;
  paymentMethod: string;
  setPaymentMethod: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  layawayDate: Date;
  setLayawayDate: (value: Date) => void;
  systemTimezone: string;

  // Totals
  cartSubtotal: number;
  cartVAT: number;
  cartTotal: number;
  vatPercentage: number;
  currencySymbol: string;

  // Actions
  createLayaway: () => void;
  isCreating: boolean;
}

export const CreateLayawayTab: React.FC<CreateLayawayTabProps> = ({
  filteredAndSortedItems,
  itemSearchTerm,
  setItemSearchTerm,
  sortBy,
  setSortBy,
  cart,
  addToCart,
  removeFromCart,
  updateCartQuantity,
  updateCartItemPrice,
  updateCartItemVat,
  customer,
  setCustomer,
  customerOptions,
  onCreateCustomer,
  productHandling,
  setProductHandling,
  paymentScheduleMonths,
  setPaymentScheduleMonths,
  initialPayment,
  setInitialPayment,
  paymentMethod,
  setPaymentMethod,
  notes,
  setNotes,
  systemTimezone,
  layawayDate,
  setLayawayDate,
  cartSubtotal,
  cartVAT,
  cartTotal,
  vatPercentage,
  currencySymbol,
  createLayaway,
  isCreating,
}) => {
  const remainingBalance = Math.max(0, cartTotal - initialPayment);
  const monthlyPayment = paymentScheduleMonths > 0 ? remainingBalance / paymentScheduleMonths : 0;
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    address: '',
  });
  const [entryItemId, setEntryItemId] = useState<string>('');
  const [entryQuantityText, setEntryQuantityText] = useState('1');
  const [entryPriceText, setEntryPriceText] = useState('');
  const [entryVatEnabled, setEntryVatEnabled] = useState(false);
  const [entryActiveField, setEntryActiveField] = useState<'name' | null>(null);
  const [entrySuggestionIndex, setEntrySuggestionIndex] = useState(0);
  const entrySuggestionsRef = useRef<HTMLDivElement>(null);

  const entrySuggestions = useMemo(() => {
    const query = itemSearchTerm.trim().toLowerCase();
    if (!query) return [];
    return filteredAndSortedItems
      .filter((item) => {
        const name = item?.name?.toLowerCase?.() || '';
        const sku = item?.sku?.toLowerCase?.() || '';
        const barcode = (item?.barcode || '').toLowerCase();
        return name.includes(query) || sku.includes(query) || barcode.includes(query);
      })
      .slice(0, 12);
  }, [filteredAndSortedItems, itemSearchTerm]);

  const selectedEntryItem = useMemo(() => {
    if (entryItemId) {
      return filteredAndSortedItems.find((item) => item.id === entryItemId) || null;
    }

    const query = itemSearchTerm.trim().toLowerCase();
    if (!query) return null;
    return (
      filteredAndSortedItems.find((item) => (item.name || '').toLowerCase() === query) ||
      filteredAndSortedItems.find((item) => (item.sku || '').toLowerCase() === query) ||
      filteredAndSortedItems.find((item) => (item.barcode || '').toLowerCase() === query) ||
      null
    );
  }, [entryItemId, filteredAndSortedItems, itemSearchTerm]);

  const toPrice = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const toQty = (value: string) => {
    const parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(1, parsed);
  };

  const selectedEntrySku = selectedEntryItem?.sku || '';
  const selectedEntryBarcode = selectedEntryItem?.barcode || '';

  const entryLineTotal = useMemo(() => {
    const unitPrice = toPrice(entryPriceText);
    const quantity = toQty(entryQuantityText);
    const subtotal = unitPrice * quantity;
    if (!entryVatEnabled || vatPercentage <= 0) return subtotal;
    return subtotal + (subtotal * vatPercentage) / 100;
  }, [entryPriceText, entryQuantityText, entryVatEnabled, vatPercentage]);

  useEffect(() => {
    if (entryActiveField === 'name' && entrySuggestions.length > 0 && entrySuggestionsRef.current) {
      const target = entrySuggestionsRef.current.querySelector<HTMLElement>(
        `[data-suggestion-index="${entrySuggestionIndex}"]`
      );
      target?.scrollIntoView({ block: 'nearest' });
    }
  }, [entryActiveField, entrySuggestionIndex, entrySuggestions.length]);

  useEffect(() => {
    setEntrySuggestionIndex(0);
  }, [itemSearchTerm]);

  const handleSelectEntryItem = (item: any) => {
    if (!item) return;
    setEntryItemId(item.id);
    setItemSearchTerm(item.name || '');
    const defaultPrice = Number(item.selling_price);
    setEntryPriceText(Number.isFinite(defaultPrice) ? defaultPrice.toString() : '');
    setEntryQuantityText('1');
    setEntryVatEnabled(Boolean(item.vat_enabled));
    setEntryActiveField(null);
  };

  const handleCommitEntryItem = () => {
    if (!selectedEntryItem) {
      return;
    }

    const quantityToAdd = toQty(entryQuantityText);
    const unitPrice = toPrice(entryPriceText);
    const availableQty = selectedEntryItem.availableQuantity ?? 0;
    const inCartQty = cart.find((cartItem: any) => cartItem.id === selectedEntryItem.id)?.quantity ?? 0;

    if (quantityToAdd <= 0) {
      return;
    }

    if (availableQty <= 0) {
      return;
    }

    if (inCartQty + quantityToAdd > availableQty) {
      return;
    }

    addToCart(selectedEntryItem, quantityToAdd, unitPrice, entryVatEnabled);
    setEntryItemId('');
    setEntryQuantityText('1');
    setEntryPriceText('');
    setEntryVatEnabled(false);
    setEntryActiveField(null);
    setItemSearchTerm('');
    setEntrySuggestionIndex(0);
  };

  const lineTotal = (price: number, quantity: number, vatEnabled?: boolean) => {
    const subtotal = (Number(price) || 0) * (Number(quantity) || 0);
    if (!vatEnabled || vatPercentage <= 0) return subtotal;
    return subtotal + (subtotal * vatPercentage) / 100;
  };

  const handleSelectCustomer = (customerId: string) => {
    setSelectedCustomerId(customerId);
    const selected = customerOptions.find((option) => option.id === customerId);
    if (!selected) return;
    setCustomer({
      name: selected.name || '',
      phone: selected.phone || '',
      address: selected.address || '',
    });
  };

  const handleSaveCustomer = () => {
    if (!newCustomer.name.trim()) return;
    const payload = {
      name: newCustomer.name.trim(),
      phone: newCustomer.phone.trim(),
      address: newCustomer.address.trim(),
    };
    onCreateCustomer(payload);
    setCustomer(payload);
    setSelectedCustomerId('');
    setNewCustomer({ name: '', phone: '', address: '' });
    setShowAddCustomerDialog(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Customer Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Select Customer *</Label>
            <div className="flex gap-2">
              <Select value={selectedCustomerId} onValueChange={handleSelectCustomer}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Choose existing customer" />
                </SelectTrigger>
                <SelectContent>
                  {customerOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}{option.phone ? ` - ${option.phone}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={() => setShowAddCustomerDialog(true)}>
                Add
              </Button>
            </div>
          </div>
          <div>
            <Label>Name *</Label>
            <Input
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              placeholder="Customer name"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={customer.phone}
              onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
              placeholder="Phone number"
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={customer.address}
              onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
              placeholder="Address"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Debtor Date</Label>
            <DateTimePicker
              value={layawayDate}
              onChange={(date) => date && setLayawayDate(date)}
              timezone={systemTimezone}
              showTime={false}
            />
          </div>

          <div>
            <Label>Product Handling</Label>
            <RadioGroup value={productHandling} onValueChange={(value: any) => setProductHandling(value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hold" id="hold" />
                <Label htmlFor="hold">Hold Product (Reserve in store)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="handover" id="handover" />
                <Label htmlFor="handover">Handover Product (Customer takes now)</Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label>Payment Schedule (Months)</Label>
            <Select value={paymentScheduleMonths.toString()} onValueChange={(v) => setPaymentScheduleMonths(parseInt(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <SelectItem key={m} value={m.toString()}>{m} month{m > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Initial Payment</Label>
            <Input
              type="number"
              step="0.01"
              value={initialPayment === 0 ? '' : initialPayment}
              onChange={(e) => {
                const value = e.target.value;
                setInitialPayment(value === '' ? 0 : (parseFloat(value) || 0));
              }}
              placeholder="0.00"
            />
          </div>

          <div>
            <Label>Payment Method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="mobile">Mobile Money</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={3}
            />
          </div>

          {cart.length > 0 && customer.name && (
            <div className="bg-blue-50 rounded-lg p-3 space-y-1">
              <div className="font-semibold text-blue-900 mb-2">Summary</div>
              <div className="text-sm text-blue-800 grid grid-cols-2 gap-2">
                <div>Total Amount:</div>
                <div className="font-medium">{currencySymbol}{cartTotal.toFixed(2)}</div>
                <div>Initial Payment:</div>
                <div className="font-medium">{currencySymbol}{initialPayment.toFixed(2)}</div>
                <div>Remaining:</div>
                <div className="font-medium">{currencySymbol}{remainingBalance.toFixed(2)}</div>
                <div>Monthly Payment:</div>
                <div className="font-medium">{currencySymbol}{monthlyPayment.toFixed(2)}</div>
              </div>
            </div>
          )}

          <Button
            className="w-full"
            onClick={createLayaway}
            disabled={cart.length === 0 || !customer.name.trim() || isCreating || initialPayment > cartTotal}
          >
            {isCreating ? 'Creating...' : 'Create Debtor'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Invoice Items
            </CardTitle>
            <span className="text-sm font-semibold">Total: {currencySymbol}{cartTotal.toFixed(2)}</span>
          </div>

          <div className="flex gap-2 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search items by name, SKU or barcode"
                value={itemSearchTerm}
                onChange={(e) => setItemSearchTerm(e.target.value)}
                onFocus={() => setEntryActiveField('name')}
                className="pl-10"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="price">Price</SelectItem>
                <SelectItem value="stock">Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {itemSearchTerm && entrySuggestions.length > 0 && entryActiveField === 'name' ? (
            <div className="relative">
              <div className="absolute left-0 top-2 z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
                <div ref={entrySuggestionsRef} className="max-h-60 overflow-y-auto">
                  {entrySuggestions.map((item, index) => (
                    <button
                      type="button"
                      key={item.id}
                      data-suggestion-index={index}
                      className={`w-full px-3 py-2 text-left text-sm ${
                        index === entrySuggestionIndex ? 'bg-slate-100' : 'hover:bg-slate-100'
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSelectEntryItem(item)}
                    >
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-slate-500">
                        {item.sku}
                        {item.barcode ? ` · ${item.barcode}` : ''}
                        {item.availableQuantity !== undefined ? ` · Stock ${item.availableQuantity}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </CardHeader>

        <CardContent>
          <div className="overflow-x-auto border rounded-lg">
            <Table wrapperClassName="overflow-visible">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[360px]">Item</TableHead>
                  <TableHead className="w-[120px]">SKU</TableHead>
                  <TableHead className="w-[140px]">Barcode</TableHead>
                  <TableHead className="w-[70px] text-center">VAT</TableHead>
                  <TableHead className="w-[90px] text-center">Qty</TableHead>
                  <TableHead className="w-[120px] text-right">Unit Price</TableHead>
                  <TableHead className="w-[120px] text-right">Total</TableHead>
                  <TableHead className="w-[56px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Input
                      value={itemSearchTerm}
                      onChange={(e) => {
                        setEntryItemId('');
                        setItemSearchTerm(e.target.value);
                        setEntryActiveField('name');
                      }}
                      onKeyDown={(event) => {
                        if (!entrySuggestions.length) return;
                        if (event.key === 'ArrowDown') {
                          event.preventDefault();
                          setEntrySuggestionIndex((prev) => (prev + 1) % entrySuggestions.length);
                        }
                        if (event.key === 'ArrowUp') {
                          event.preventDefault();
                          setEntrySuggestionIndex((prev) => (prev - 1 + entrySuggestions.length) % entrySuggestions.length);
                        }
                        if (event.key === 'Enter') {
                          const selected = entrySuggestions[entrySuggestionIndex];
                          if (selected) {
                            event.preventDefault();
                            handleSelectEntryItem(selected);
                          }
                        }
                      }}
                      placeholder="Start typing item name"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      autoCapitalize="none"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                    />
                  </TableCell>
                  <TableCell>
                    <Input value={selectedEntrySku} readOnly />
                  </TableCell>
                  <TableCell>
                    <Input value={selectedEntryBarcode} readOnly />
                  </TableCell>
                  <TableCell className="text-center">
                    <Checkbox
                      checked={entryVatEnabled}
                      onCheckedChange={(checked) => setEntryVatEnabled(checked === true)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="1"
                      className="w-20 mx-auto text-center"
                      value={entryQuantityText}
                      onChange={(e) => setEntryQuantityText(e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-24 ml-auto text-right"
                      value={entryPriceText}
                      onChange={(e) => setEntryPriceText(e.target.value)}
                    />
                  </TableCell>
                  <TableCell className="text-right font-semibold">{currencySymbol}{entryLineTotal.toFixed(2)}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={handleCommitEntryItem}
                      disabled={
                        !selectedEntryItem ||
                        toQty(entryQuantityText) <= 0 ||
                        toPrice(entryPriceText) < 0
                      }
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>

                {cart.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="font-medium text-sm">{item.name}</div>
                    </TableCell>
                    <TableCell className="text-gray-600">{item.sku}</TableCell>
                    <TableCell className="text-gray-600">{item.barcode || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={Boolean(item.vat_enabled)}
                        onCheckedChange={(checked) => updateCartItemVat(item.id, checked === true)}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="1"
                        className="w-20 mx-auto text-center"
                        value={item.quantity}
                        onChange={(e) =>
                          updateCartQuantity(item.id, parseInt(e.target.value) || 1)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-24 ml-auto text-right"
                        value={item.price}
                        onChange={(e) =>
                          updateCartItemPrice(item.id, parseFloat(e.target.value) || 0)
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {currencySymbol}
                      {lineTotal(parseFloat(item.price.toString()), item.quantity, item.vat_enabled).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="border-t pt-3 mt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span>{currencySymbol}{cartSubtotal.toFixed(2)}</span>
            </div>
            {cartVAT > 0 && (
              <div className="flex justify-between text-sm">
                <span>VAT ({vatPercentage}%):</span>
                <span>{currencySymbol}{cartVAT.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold">
              <span>Total:</span>
              <span>{currencySymbol}{cartTotal.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showAddCustomerDialog} onOpenChange={setShowAddCustomerDialog}>
        <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="Phone number"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={newCustomer.address}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Address"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddCustomerDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCustomer} disabled={!newCustomer.name.trim()}>
                Save Customer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
